import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Zap, Package, Star, CheckCircle, ArrowLeft, Send, Loader2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USAGE_TYPES = [
  "Bireysel Kullanıcı",
  "İşveren / İşyeri",
  "Belediye / Kamu Kurumu",
  "Sağlık / Eğitim Kurumu",
  "Eczane / Medikal Firma",
  "Diğer",
];

const ProductPage = ({ calculationData, result, onBack }) => {
  const [product, setProduct] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [usageType, setUsageType] = useState("");
  const [interestedInProduct, setInterestedInProduct] = useState(true);
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, []);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/product`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Lütfen adınızı girin");
      return;
    }

    if (!email.trim() || !email.includes("@")) {
      toast.error("Lütfen geçerli bir e-posta adresi girin");
      return;
    }

    if (!phone.trim()) {
      toast.error("Lütfen telefon numaranızı girin");
      return;
    }

    if (!usageType) {
      toast.error("Lütfen kullanım tipi seçin");
      return;
    }

    if (!kvkkConsent) {
      toast.error("KVKK onayı gereklidir");
      return;
    }

    setLoading(true);

    try {
      // Lead kaydı
      const payload = {
        lead_data: {
          name,
          email,
          usage_type: usageType,
          other_usage: null,
          kvkk_consent: kvkkConsent,
          marketing_consent: true,
        },
        calculation_data: calculationData,
      };

      await axios.post(`${API}/calculate`, payload);

      if (interestedInProduct && product) {
        // WhatsApp sipariş linki oluştur
        const message = `Merhaba! Alcodot test çubuğu sipariş vermek istiyorum.%0A%0A*İletişim:*%0Aİsim: ${name}%0AEmail: ${email}%0ATelefon: ${phone}%0AAdet: ${quantity}%0A%0AToplam: ${(product.price * quantity).toFixed(2)} TL`;
        const whatsappUrl = `https://wa.me/905XXXXXXXXX?text=${message}`;
        
        toast.success("Bilgileriniz kaydedildi! WhatsApp'a yönlendiriliyorsunuz...");
        
        setTimeout(() => {
          window.open(whatsappUrl, '_blank');
          window.location.href = '/thank-you';
        }, 1500);
      } else {
        toast.success("Bilgileriniz başarıyla kaydedildi!");
        setTimeout(() => {
          window.location.href = '/thank-you';
        }, 1000);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Back button */}
      <Button
        onClick={onBack}
        variant="ghost"
        className="text-teal-400 hover:bg-teal-500/10"
        data-testid="back-from-product-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Geri Dön
      </Button>

      {/* Product Hero */}
      <div className="glass-card-premium rounded-3xl p-10 neon-border text-center">
        <div className="inline-block mb-8">
          {product.image_url ? (
            <div className="relative inline-block">
              <img 
                src={product.image_url} 
                alt="Alcodot Test Çubuğu" 
                className="w-64 h-64 object-contain mx-auto rounded-3xl"
                style={{ filter: 'drop-shadow(0 20px 40px rgba(20, 184, 166, 0.3))' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = 'w-40 h-40 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-3xl flex items-center justify-center shadow-2xl neon-glow pulse-glow mx-auto';
                  fallback.innerHTML = '<svg class="w-20 h-20 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>';
                  e.currentTarget.parentElement.appendChild(fallback);
                }}
              />
              <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-black px-3 py-1.5 rounded-full animate-pulse shadow-lg">
                HOT
              </div>
            </div>
          ) : (
            <div className="w-40 h-40 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-3xl flex items-center justify-center shadow-2xl neon-glow pulse-glow mx-auto relative">
              <Shield className="w-20 h-20 text-white" />
              <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-black px-3 py-1.5 rounded-full animate-pulse shadow-lg">
                HOT
              </div>
            </div>
          )}
        </div>

        <h2 className="text-5xl font-black text-gradient-glow mb-4">Alcodot Test Çubuğu</h2>
        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
          {product.description}
        </p>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
          {product.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-3 bg-gray-900/50 p-4 rounded-xl border border-teal-500/20">
              <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0" />
              <span className="text-gray-200 text-sm font-medium">{feature}</span>
            </div>
          ))}
        </div>

        {/* Social Proof */}
        <div className="grid grid-cols-3 gap-6 mb-8 max-w-2xl mx-auto">
          <div className="bg-gray-900/50 p-5 rounded-2xl border border-teal-500/20">
            <p className="text-3xl font-black text-white mb-1">10.000+</p>
            <p className="text-xs text-gray-400">Mutlu Kullanıcı</p>
          </div>
          <div className="bg-gray-900/50 p-5 rounded-2xl border border-teal-500/20">
            <div className="flex justify-center mb-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-xs text-gray-400">5/5 Mükemmel</p>
          </div>
          <div className="bg-gray-900/50 p-5 rounded-2xl border border-teal-500/20">
            <p className="text-3xl font-black text-white mb-1">24 Saat</p>
            <p className="text-xs text-gray-400">Hızlı Kargo</p>
          </div>
        </div>

        {/* Price */}
        <div className="mb-8">
          <p className="text-sm text-gray-400 mb-2">Özel Lansman Fiyatı</p>
          <div className="flex items-center justify-center gap-4">
            <p className="text-6xl font-black text-gradient-glow">{product.price} TL</p>
            {product.discount_text && (
              <div className="bg-red-500/20 border-2 border-red-500/40 rounded-xl px-4 py-2">
                <p className="text-red-400 font-bold text-sm">{product.discount_text}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Form */}
      <div className="glass-card-premium rounded-3xl p-10 neon-border">
        <h3 className="text-3xl font-black text-gradient mb-8 text-center">Sipariş Bilgileri</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div className="space-y-3">
              <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">Ad Soyad *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Adınız Soyadınız"
                className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
                data-testid="product-name-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-3">
              <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">E-posta *</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@email.com"
                className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
                data-testid="product-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Phone */}
            <div className="space-y-3">
              <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">Telefon *</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="05XX XXX XX XX"
                className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
                data-testid="product-phone-input"
              />
            </div>

            {/* Quantity */}
            <div className="space-y-3">
              <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">Adet</Label>
              <Input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg text-center font-bold"
                data-testid="product-quantity-input"
              />
            </div>
          </div>

          {/* Usage Type */}
          <div className="space-y-3">
            <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">Kullanım Amacı *</Label>
            <Select value={usageType} onValueChange={setUsageType}>
              <SelectTrigger className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white" data-testid="product-usage-select">
                <SelectValue placeholder="Seçiniz" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-teal-500/30">
                {USAGE_TYPES.map((type) => (
                  <SelectItem key={type} value={type} className="text-white hover:bg-teal-500/20">
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Total */}
          <div className="bg-teal-500/10 border-2 border-teal-500/30 rounded-2xl p-6 text-center">
            <p className="text-sm text-teal-400 mb-2 font-bold uppercase">Toplam Tutar</p>
            <p className="text-5xl font-black text-white">{(product.price * quantity).toFixed(2)} TL</p>
            <p className="text-xs text-gray-400 mt-2">✓ Ücretsiz kargo dahil</p>
          </div>

          {/* KVKK */}
          <div className="bg-gray-900/30 p-6 rounded-2xl border-2 border-teal-500/20">
            <div className="flex items-start space-x-4">
              <Checkbox
                id="kvkk-product"
                checked={kvkkConsent}
                onCheckedChange={setKvkkConsent}
                className="mt-1 border-teal-500/50 data-[state=checked]:bg-teal-500"
                data-testid="product-kvkk-checkbox"
              />
              <Label htmlFor="kvkk-product" className="text-sm text-gray-300 cursor-pointer leading-relaxed">
                <span className="font-bold text-teal-400">KVKK Aydınlatma Metni *</span>
                <br />
                Kişisel verilerimin işlenmesine ilişkin aydınlatma metnini okudum ve anladım.
                Sipariş sürecinde verilerimin işlenmesini kabul ediyorum.
              </Label>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-18 rounded-2xl bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 hover:from-teal-400 hover:via-cyan-400 hover:to-sky-400 text-black font-black text-2xl shadow-lg neon-glow smooth-transition shimmer py-6"
            data-testid="submit-product-order-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-7 h-7 mr-3 animate-spin" />
                Gönderiliyor...
              </>
            ) : (
              <>
                <ShoppingCart className="w-7 h-7 mr-3" />
                Sipariş Ver (WhatsApp)
                <ArrowLeft className="w-7 h-7 ml-3 rotate-180" />
              </>
            )}
          </Button>

          <p className="text-center text-xs text-gray-500">
            ✓ Güvenli sipariş · ✓ 14 gün iade garantisi · ✓ Hızlı teslimat
          </p>
        </form>
      </div>
    </div>
  );
};

export default ProductPage;